import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import Select2Dropdown from '../common/Select2Dropdown';
import { openFilePreview } from '../common/FilePreviewModal';
import ConfirmDialog from '../common/ConfirmDialog';
import PopoutWindow from '../common/PopoutWindow';
import PayableExpensesView from './PayableExpensesView';
import VendorForm from '../masters/VendorForm';
import '../masters/VendorList.css';
import { usePermissions } from '../../services/PermissionsContext';
import { TRANSPORTER_CASH_PAYABLE_CHARGES } from '../../transporterPayableChargesConfig';

const API_PAYABLES = '/api/admin/other-payables/';
const API_JOB_SEARCH = '/api/documentation/jobs/search_by_idf/';
const API_STAFF = '/api/admin/staff/';

const JOB_SEARCH_FIELDS = [
    { id: 'job_no', text: 'JOB NO.' },
    { id: 'bl_no', text: 'BL NO.' },
    { id: 'idf_no', text: 'IDF NO.' },
    { id: 'kk_file_no', text: 'KK FILE NO.' },
];

const VENDOR_STAFF_OPTIONS = [
    { value: 'VENDOR', label: 'VENDOR' },
    { value: 'STAFF', label: 'STAFF' },
];

// Authentication helper moved into component to support transporter token

// Helper for currency formatting
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
};

const isWhtCharge = (chargeName) => {
    if (!chargeName) return false;
    return String(chargeName).toUpperCase().includes('WHT');
};

const addCommasToInput = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    const clean = val.toString().replace(/,/g, '');
    const parts = clean.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join('.');
};

const formatDateTimeDisplay = (value) => {
    if (!value) return '-';
    const dt = new Date(value);
    if (isNaN(dt.getTime())) return String(value);
    return dt.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const getTransferCharge = (amount, brackets) => {
    const numericAmount = parseFloat(amount);
    if (!numericAmount || Number.isNaN(numericAmount) || numericAmount <= 0) return 0;

    const normalizedBrackets = Array.isArray(brackets) ? brackets : [];
    const matchedBracket = normalizedBrackets.find(bracket => {
        const minAmount = parseFloat(bracket.min_amount ?? bracket.min ?? bracket.minimum_amount ?? 0);
        const maxValue = bracket.max_amount ?? bracket.max ?? bracket.maximum_amount;
        const maxAmount = maxValue === null || maxValue === '' || maxValue === undefined ? Infinity : parseFloat(maxValue);
        return numericAmount >= minAmount && numericAmount <= maxAmount;
    });

    return matchedBracket ? parseFloat(matchedBracket.rate ?? matchedBracket.amount ?? matchedBracket.charge ?? matchedBracket.charge_amount ?? 0) : 0;
};

const formatDateDisplay = (value) => {
    if (!value) return '-';
    const str = String(value);
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[3]}/${match[2]}/${match[1]}`;
    const dt = new Date(value);
    if (isNaN(dt.getTime())) return str;
    return dt.toLocaleDateString('en-GB');
};

// Component for formatted number input
const FormattedNumberInput = ({ value, onChange, disabled, style, required, placeholder, allowNegative = false }) => {
    const [displayValue, setDisplayValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (!isFocused) {
            if (value !== '' && value !== null && !isNaN(value)) {
                setDisplayValue(formatCurrency(value));
            } else {
                setDisplayValue('');
            }
        }
    }, [value, isFocused]);

    const handleChange = (e) => {
        let val = e.target.value;
        if (allowNegative) {
            const hasMinus = val.includes('-');
            val = val.replace(/-/g, '');
            if (hasMinus) val = '-' + val;
        } else {
            val = val.replace(/-/g, '');
        }
        const regex = allowNegative ? /^-?[\d,]*\.?[\d]*$/ : /^[\d,]*\.?[\d]*$/;
        if (regex.test(val)) {
             const raw = val.replace(/,/g, '');
             onChange(raw);
             setDisplayValue(addCommasToInput(val));
        }
    };

    const handleBlur = () => {
        setIsFocused(false);
        if (value !== '' && value !== null && !isNaN(value)) {
            setDisplayValue(formatCurrency(value));
        }
    };

    const handleFocus = () => {
        setIsFocused(true);
        if (value !== '' && value !== null && !isNaN(value)) {
            setDisplayValue(addCommasToInput(value));
        }
    };

    return (
        <input
            type="text"
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            disabled={disabled}
            style={{ ...style, textAlign: 'right' }}
            required={required}
            className="form-control"
            placeholder={placeholder}
        />
    );
};

const OfficePayablesForm = ({ 
    mode, 
    initialData, 
    onClose, 
    onSave,
    module,
    isTransporter: propIsTransporter,
    adminForTransporter,
    popoutMode,
    toastContainerId,
    isTransporterPortal: propIsTransporterPortal,
    approvalMode,
    canApprove,
    onApprove,
    onReject,
    rejectReason,
    setRejectReason,
    showRejectInput,
    setShowRejectInput,
    actionLoading,
    receiptUploadOnly,
    hideClientPaid,
}) => {
    const toastOptions = toastContainerId ? { containerId: toastContainerId } : undefined;
    const notifyError = (message) => toast.error(message, toastOptions);
    const notifySuccess = (message) => toast.success(message, toastOptions);

    const isTransporter = propIsTransporter || !!(initialData?.transporter);
    const isTransporterPortal = !!propIsTransporterPortal;
    const getAuthHeaders = (isForm = false) => {
        const tokenKey = adminForTransporter ? 'kk_token' : (isTransporter ? 'tp_token' : 'kk_token');
        let token = localStorage.getItem(tokenKey);
        if (!token) {
            token = tokenKey === 'tp_token'
                ? localStorage.getItem('kk_token')
                : localStorage.getItem('tp_token');
        }
        const headers = {
            'Authorization': `Bearer ${token || ''}`
        };
        if (!isForm) {
            headers['Content-Type'] = 'application/json';
        }
        return headers;
    };

    // Refresh transporter token on mount and periodically inside the popup
    useEffect(() => {
        const tokenKey = adminForTransporter ? 'kk_token' : (isTransporter ? 'tp_token' : 'kk_token');
        if (tokenKey !== 'tp_token') return;

        const refresh = async () => {
            const refreshToken = localStorage.getItem('tp_refresh_token');
            if (!refreshToken) return;
            try {
                const res = await fetch('/api/login/refresh/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refresh: refreshToken }),
                });
                const data = await res.json();
                if (res.ok && data?.access) {
                    localStorage.setItem('tp_token', data.access);
                    if (data.refresh) localStorage.setItem('tp_refresh_token', data.refresh);
                } else {
                    console.error('Transporter token refresh failed:', data);
                }
            } catch (e) {
                console.error('Transporter token refresh error:', e);
            }
        };

        refresh();
        const interval = setInterval(refresh, 4 * 60 * 1000);
        return () => clearInterval(interval);
    }, [adminForTransporter, isTransporter]);

    const isView = mode === 'view';
    const isFieldDisabled = isView || receiptUploadOnly;
    const displayAsView = isView || receiptUploadOnly;

    const [documentTypeOptions, setDocumentTypeOptions] = useState([]);

    const [jobDetails, setJobDetails] = useState(null);
    const [jobSearchField, setJobSearchField] = useState(isTransporter ? 'idf_no' : '');
    const [jobSearchTerm, setJobSearchTerm] = useState('');
    const [idfOptions, setIdfOptions] = useState([]);
    const [activeDropdownIndex, setActiveDropdownIndex] = useState(-1);

    // For transporter cash payables, lock job search to IDF
    useEffect(() => {
        if (isTransporter) {
            setFormData(prev => ({ ...prev, invoice_type: prev.invoice_type || 'VOUCHER' }));
            if (jobSearchField !== 'idf_no') {
                setJobSearchField('idf_no');
                setJobSearchTerm('');
                setIdfOptions([]);
                setJobDetails(null);
            }
        }
    }, [isTransporter]);
    const [loadingInfo, setLoadingInfo] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Dynamic Options
    const [chargeOptions, setChargeOptions] = useState([]);
    const [vendorOptions, setVendorOptions] = useState([]);
    const [staffOptions, setStaffOptions] = useState([]);
    const [tempStaffOptions, setTempStaffOptions] = useState([]); // For staff not saved to staff table
    const [tempStaffName, setTempStaffName] = useState(''); // For TEMP staff name input

    // Inline vendor creation — the + button beside the vendor dropdown
    const permCtx = usePermissions();
    const canCreateVendor = !isTransporter && !isFieldDisabled && !!permCtx?.hasPermission?.('VENDORS', 'can_create');
    const [showVendorForm, setShowVendorForm] = useState(false);

    const refreshVendorOptions = async (savedVendor) => {
        try {
            const rawModule = (module || 'OFFICE').toUpperCase();
            const moduleUpper = rawModule === 'ACCOUNTS' ? 'ACCOUNTANT' : rawModule;
            const moduleParam = moduleUpper ? `?module=${encodeURIComponent(moduleUpper)}` : '';
            const res = await fetch(`${API_PAYABLES}get_options/${moduleParam}`, { headers: getAuthHeaders() });
            if (!res.ok) return;
            const data = await res.json();
            const sorted = [...(data.vendors || [])].sort((a, b) => a.text.localeCompare(b.text));
            // Ensure the newly created vendor is present even if get_options lags
            if (savedVendor?.id && !sorted.some(v => String(v.id) === String(savedVendor.id))) {
                sorted.push({ id: savedVendor.id, text: savedVendor.name || String(savedVendor.id) });
                sorted.sort((a, b) => a.text.localeCompare(b.text));
            }
            setVendorOptions(sorted);
        } catch (e) {
            console.error('Failed to refresh vendors', e);
        }
    };

    const handleVendorFormClose = async (saved, savedVendor) => {
        setShowVendorForm(false);
        if (saved) {
            await refreshVendorOptions(savedVendor);
            if (savedVendor?.id) setFormData(prev => ({ ...prev, vendor: savedVendor.id }));
        }
    };

    // Form Fields
    const [formData, setFormData] = useState({
        job: '',
        vendor_staff_type: '', // VENDOR or STAFF
        vendor: '',
        staff: '',
        staff_name: '',
        payment_date: new Date().toISOString().split('T')[0],
        remarks: '',
        currency: 'TZS', // Always TZS
        pay_by_control_no: false,
        control_no: '',
        control_no_type: '',
        invoice_type: '',
        invoice_no: '',
        invoice_date: '',
    });

    const [items, setItems] = useState(() =>
        !initialData ? [{ charge_name: '', amount: 0, add_vat: false, vat_amount: 0, total_amount: 0, is_cash: false, requires_document: false }] : []
    );

    const [documents, setDocuments] = useState([]);
    const [docIdsToDelete, setDocIdsToDelete] = useState([]);

    const [totalPayable, setTotalPayable] = useState(0);
    const [netAmount, setNetAmount] = useState(0);
    const [totalVat, setTotalVat] = useState(0);

    // Approval state
    const [rejectionTarget, setRejectionTarget] = useState(null);
    const [showApprovalHistory, setShowApprovalHistory] = useState(true);

    const isStage1Approval = approvalMode && initialData?.status === 'PENDING';
    const isStage2Approval = approvalMode && initialData?.status === 'APPROVED 1';
    const isStage3Approval = approvalMode && initialData?.status === 'APPROVED 2';
    const isExpensesView = initialData && (initialData.status === 'APPROVED 3' || initialData.status === 'CLIENT PAID' || initialData.status === 'CLIENT_PAID');
    const isUploadedReceiptReview = !!(
        approvalMode &&
        canApprove &&
        hideClientPaid &&
        isView &&
        !receiptUploadOnly &&
        ['APPROVED 3', 'CLIENT PAID', 'CLIENT_PAID'].includes(initialData?.status) &&
        initialData?.receipt_uploaded &&
        !initialData?.receipt_approved
    );

    // Stage 2 payment fields
    const [stage2Data, setStage2Data] = useState({
        bank: initialData?.bank_account || '',
        cheque_no: initialData?.cheque_no || '',
        collected_by: initialData?.collected_by || '',
        paid_by: initialData?.paid_by || '',
    });
    // Stage 3 payment fields
    const [stage3Data, setStage3Data] = useState({
        payment_date: initialData?.payment_date || new Date().toISOString().split('T')[0],
        tiss_tips: (initialData?.tiss_tips && initialData?.tiss_tips !== 'NO TISS/TIPS') ? initialData?.tiss_tips : '',
        payment_source: initialData?.bank_account || '',
    });
    const [showStage3TissError, setShowStage3TissError] = useState(false);
    const [bankOptions, setBankOptions] = useState([]);

    const isTissOrTipsSelected = stage3Data.tiss_tips === 'TISS' || stage3Data.tiss_tips === 'TIPS';

    const filteredBankOptions = useMemo(() => {
        return bankOptions.filter(b => b.currency === 'TZS');
    }, [bankOptions]);

    const selectedStage3BankAccount = useMemo(() => {
        const id = stage3Data.payment_source || initialData?.bank_account || '';
        if (!id || id === 'CASH') return null;
        return bankOptions.find(b => String(b.id) === String(id)) || null;
    }, [bankOptions, stage3Data.payment_source, initialData?.bank_account]);

    const selectedStage3Brackets = useMemo(() => {
        if (!selectedStage3BankAccount || !isTissOrTipsSelected) return [];
        return stage3Data.tiss_tips === 'TIPS'
            ? (selectedStage3BankAccount.tips_brackets || [])
            : (selectedStage3BankAccount.tiss_brackets || []);
    }, [selectedStage3BankAccount, stage3Data.tiss_tips, isTissOrTipsSelected]);

    const stage3BasePayableAmount = useMemo(() => {
        return parseFloat(totalPayable) || 0;
    }, [totalPayable]);

    const stage3TissTipsAmount = useMemo(() => {
        return getTransferCharge(stage3BasePayableAmount, selectedStage3Brackets);
    }, [stage3BasePayableAmount, selectedStage3Brackets]);

    const isGepgPayable = !!(formData.control_no && formData.control_no_type === 'GEPG');
    const isCash = (stage3Data.payment_source || initialData?.bank_account || '') === 'CASH';

    const gepgBrackets = useMemo(() => {
        if (!isGepgPayable || !selectedStage3BankAccount) return [];
        return selectedStage3BankAccount.gepg_brackets || [];
    }, [isGepgPayable, selectedStage3BankAccount]);

    const gepgAmount = useMemo(() => {
        return getTransferCharge(stage3BasePayableAmount, gepgBrackets);
    }, [stage3BasePayableAmount, gepgBrackets]);

    const stage3NewPayableTotal = useMemo(() => {
        return stage3BasePayableAmount + stage3TissTipsAmount + gepgAmount;
    }, [stage3BasePayableAmount, stage3TissTipsAmount, gepgAmount]);

    const showPaymentSection = !isTransporterPortal && (isStage2Approval || (isStage3Approval && mode !== 'view') || (mode === 'view' && initialData?.status === 'APPROVED 2' && stage2Data.collected_by) || isExpensesView ||
        ['APPROVED 1', 'APPROVED 2', 'APPROVED 3', 'CLIENT PAID', 'CLIENT_PAID'].includes(initialData?.status));
    const isReadOnlyPayment = isStage3Approval || isExpensesView || (isView && !approvalMode) || receiptUploadOnly;

    const getRejectionOptions = () => {
        if (isStage2Approval) return [{ id: 'CREATOR', text: 'CREATOR' }, { id: 'STAGE 1', text: 'STAGE 1' }];
        if (isStage3Approval) return [{ id: 'CREATOR', text: 'CREATOR' }, { id: 'STAGE 1', text: 'STAGE 1' }, { id: 'STAGE 2', text: 'STAGE 2' }];
        return [];
    };

    const executeRejectionSubmit = () => {
        if (!rejectReason) { notifyError('Reason required'); return; }
        let target = 'REJECTED';
        if (isStage2Approval) {
            if (!rejectionTarget) { notifyError('Select Rejection Target'); return; }
            if (rejectionTarget === 'STAGE 1') target = 'PENDING';
        } else if (isStage3Approval) {
            if (!rejectionTarget) { notifyError('Select Rejection Target'); return; }
            if (rejectionTarget === 'STAGE 1') target = 'PENDING';
            if (rejectionTarget === 'STAGE 2') target = 'APPROVED 1';
        }
        if (onReject) onReject(rejectReason, target);
    };

    // Confirmation Dialog
    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

    const showConfirm = (title, message, onConfirm) => {
        setConfirmDialog({ open: true, title, message, onConfirm });
    };

    const closeConfirm = () => {
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
    };

    const getJobSearchValue = (job, field) => {
        if (!job) return '';
        if (field === 'job_no') return job.id || '';
        if (field === 'bl_no') return job.bl_no || '';
        if (field === 'kk_file_no') return job.kk_file_no || '';
        return job.idf_no || '';
    };

    const getDefaultReadOnlySearchInfo = () => {
        if (!jobDetails && !initialData) {
            return { field: 'idf_no', value: '' };
        }

        const data = jobDetails || {};
        const idf = data.idf_no || initialData?.idf_number || '';
        const jobNo = data.id || initialData?.job_number || '';
        const bl = data.bl_no || initialData?.bl_number || '';
        const kkFile = data.kk_file_no || initialData?.file_no || '';

        if (idf) return { field: 'idf_no', value: idf };
        if (jobNo) return { field: 'job_no', value: jobNo };
        if (bl) return { field: 'bl_no', value: bl };
        if (kkFile) return { field: 'kk_file_no', value: kkFile };
        return { field: 'idf_no', value: '' };
    };

    // Fetch Options
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                if (isTransporter) {
                    // Transporter cash charges come from PayableCharge master data
                    // (TRANSPORTER department, cash flag) — the hardcoded list is a
                    // fallback in case none have been configured yet.
                    const tres = await fetch(`${API_PAYABLES}get_options/?module=TRANSPORTER`, { headers: getAuthHeaders() });
                    if (tres.ok) {
                        const tdata = await tres.json();
                        const cashCharges = (Array.isArray(tdata.charge_names) ? tdata.charge_names : []).filter(c => c.cash === true);
                        setChargeOptions(cashCharges.length ? cashCharges : TRANSPORTER_CASH_PAYABLE_CHARGES);
                    } else {
                        setChargeOptions(TRANSPORTER_CASH_PAYABLE_CHARGES);
                    }
                    return;
                }
                const rawModule = (module || 'OFFICE').toUpperCase();
                const moduleUpper = rawModule === 'ACCOUNTS' ? 'ACCOUNTANT' : rawModule;
                const moduleParam = moduleUpper ? `?module=${encodeURIComponent(moduleUpper)}` : '';
                const res = await fetch(`${API_PAYABLES}get_options/${moduleParam}`, { headers: getAuthHeaders() });
                if (res.ok) {
                    const data = await res.json();
                    if(data.charge_names) {
                        const isAccounts = rawModule === 'ACCOUNTS';
                        // ADJUSTMENT works like OtherPayablesForm — unbounded amount,
                        // only available when the form is opened from the ACCOUNTS module.
                        // Strip it from the API-provided charges too, in case a PayableCharge
                        // named ADJUSTMENT exists in master data for this department.
                        const cashCharges = data.charge_names.filter(c =>
                            c.cash === true && (isAccounts || (c.id || '').toUpperCase() !== 'ADJUSTMENT')
                        );
                        if (!adminForTransporter && isAccounts
                            && !cashCharges.some(c => (c.id || '').toUpperCase() === 'ADJUSTMENT')) {
                            cashCharges.push({ id: 'ADJUSTMENT', text: 'ADJUSTMENT', add_vat: false, cash: true });
                        }
                        setChargeOptions(cashCharges);
                    }
                    if(data.vendors) setVendorOptions([...data.vendors].sort((a, b) => a.text.localeCompare(b.text)));
                    if(data.banks) setBankOptions(data.banks);
                }
            } catch (error) {
                console.error("Failed to fetch options", error);
            }
        };
        fetchOptions();
    }, [module, isTransporter]);

    // Fetch staff based on department (from module)
    useEffect(() => {
        const fetchStaff = async () => {
            try {
                const res = await fetch(`${API_STAFF}?department_name=${module}&active=true`, { headers: getAuthHeaders() });
                if (res.ok) {
                    const data = await res.json();
                    const options = data.map(s => ({ id: s.id, text: s.name }));
                    // Add TEMP option at the end
                    options.push({ id: 'TEMP', text: 'TEMP' });
                    setStaffOptions(options);
                    // In edit mode, match staff_name to an ID so the dropdown pre-selects correctly
                    setFormData(prev => {
                        if (prev.vendor_staff_type === 'STAFF' && prev.staff_name && !prev.staff) {
                            const match = options.find(s => s.text === prev.staff_name);
                            if (match) return { ...prev, staff: match.id };
                        }
                        return prev;
                    });
                }
            } catch (error) {
                console.error("Failed to fetch staff", error);
            }
        };
        if (module) fetchStaff();
    }, [module]);

    // Initialize logic
    useEffect(() => {
        const init = async () => {
             if (initialData) {
                setFormData({
                    job: initialData.job || initialData.job_number,
                    vendor_staff_type: isTransporter ? 'VENDOR' : (initialData.vendor_staff_type || (initialData.vendor ? 'VENDOR' : '')),
                    vendor: isTransporter ? (initialData.transporter || initialData.vendor) : (initialData.vendor || ''),
                    staff: '',
                    staff_name: initialData.staff_name || '',
                    payment_date: initialData.payment_date || '',
                    remarks: initialData.remarks || '',
                    currency: 'TZS',
                    pay_by_control_no: initialData.pay_by_control_no || false,
                    control_no: initialData.control_no || '',
                    control_no_type: initialData.control_no_type || '',
                    invoice_type: initialData.invoice_type || '',
                    invoice_no: initialData.proforma_invoice_no || initialData.tax_invoice_no || initialData.invoice_no || '',
                    invoice_date: initialData.invoice_date || '',
                });

                // Handle TEMP staff initialization
                if (initialData.staff_name && initialData.staff_name !== 'TEMP' && !initialData.staff) {
                    // If staff_name exists but it's not TEMP and no staff ID, it might be a custom staff name
                    setFormData(prev => ({ ...prev, staff: initialData.staff_name }));
                } else if (initialData.staff_name && initialData.staff_name !== 'TEMP' && initialData.staff_name.startsWith('TEMP - ')) {
                    // Handle previously saved TEMP staff with custom name
                    const tempName = initialData.staff_name.replace('TEMP - ', '');
                    setTempStaffName(tempName);
                    setFormData(prev => ({ ...prev, staff: 'TEMP', staff_name: initialData.staff_name }));
                } else if (initialData.staff === 'TEMP') {
                    // Handle TEMP selection
                    if (initialData.staff_name && initialData.staff_name !== 'TEMP') {
                        setTempStaffName(initialData.staff_name);
                        setFormData(prev => ({ ...prev, staff_name: initialData.staff_name }));
                    }
                }

                if (initialData.items && Array.isArray(initialData.items)) {
                     setItems(initialData.items.map(item => ({
                         ...item,
                         vat_amount: item.vat_amount || 0,
                         total_amount: item.total_amount || item.amount
                     })));
                     // Retrieve totals from database in edit/view mode
                     setNetAmount(initialData.net_amount || 0);
                     setTotalVat(initialData.total_vat || 0);
                     setTotalPayable(initialData.total_amount || 0);
                } else {
                     setItems(prev => prev.length === 0 ? [{ charge_name: '', amount: 0, add_vat: false, vat_amount: 0, total_amount: 0, is_cash: true, requires_document: false, units: 1, unit_type: '', unit_rate: 0 }] : prev);
                }

                if (initialData.documents && Array.isArray(initialData.documents)) {
                     setDocuments(initialData.documents.map(d => ({
                         type: d.document_type,
                         file: null,
                         existingUrl: d.document_file_url || d.document_file,
                         id: d.id,
                     })));
                }
                
                const jobId = initialData.job || initialData.job_number;
                const idfToFetch = initialData.idf_number || initialData.job_details?.idf_no;
                if (jobId) {
                    setJobSearchField('job_no');
                    setJobSearchTerm(String(jobId));
                    await fetchJobDetails(String(jobId), true, 'job_no');
                } else if (idfToFetch) {
                    setJobSearchField('idf_no');
                    setJobSearchTerm(idfToFetch);
                    await fetchJobDetails(idfToFetch, true, 'idf_no');
                }
            }
        };
        init();
    }, [initialData]);

    // Auto-check "Pay by Control No" when selected vendor has CONTROL NUMBER as a payment mode
    useEffect(() => {
        if (!formData.vendor || isFieldDisabled) return;
        const selectedVendor = vendorOptions.find(v => String(v.id) === String(formData.vendor));
        if (!selectedVendor) return;
        const modes = (selectedVendor.payment_modes || '').toUpperCase().split(',').map(m => m.trim());
        const hasControlNumber = modes.includes('CONTROL NUMBER');
        setFormData(prev => ({
            ...prev,
            pay_by_control_no: hasControlNumber,
            control_no: hasControlNumber ? prev.control_no : '',
            control_no_type: hasControlNumber ? prev.control_no_type : '',
        }));
    }, [formData.vendor, vendorOptions]);

    // Clear control no fields when unchecked
    useEffect(() => {
        if (!formData.pay_by_control_no && (formData.control_no || formData.control_no_type)) {
            setFormData(prev => ({ ...prev, control_no: '', control_no_type: '' }));
        }
    }, [formData.pay_by_control_no]);

    // Default TISS/TIPS to NO TISS/TIPS for GEPG control numbers and cash payments
    useEffect(() => {
        if ((isGepgPayable || isCash) && stage3Data.tiss_tips !== 'NO TISS/TIPS') {
            setStage3Data(prev => ({ ...prev, tiss_tips: 'NO TISS/TIPS' }));
        }
    }, [isGepgPayable, isCash]);

    // Calculate Total Payable (recalculate while editing; keep database values in read-only view)
    useEffect(() => {
        if (isView) return; // Don't recalculate in read-only view mode
        let total = 0;
        let net = 0;
        let vat = 0;
        items.forEach(item => {
            const amount = parseFloat(item.amount) || 0;
            const vatAmount = item.add_vat ? (parseFloat(item.vat_amount) || amount * 0.18) : 0;
            net += amount;
            vat += vatAmount;
            total += amount + vatAmount;
        });
        setNetAmount(net);
        setTotalVat(vat);
        setTotalPayable(total);
    }, [items, initialData]);

    // Backfill requires_document and document_types on items when chargeOptions loads (covers edit/view mode)
    useEffect(() => {
        if (!chargeOptions.length) return;
        setItems(prev => prev.map(item => {
            if (!item.charge_name) return item;
            const charge = chargeOptions.find(c => c.id === item.charge_name);
            if (!charge) return item;
            return {
                ...item,
                requires_document: charge.document_required || false,
                document_types: item.document_types || charge.document_types || '',
            };
        }));
    }, [chargeOptions]);

    // Update document type options based on charges that require documents
    useEffect(() => {
        const defaultTypes = isTransporter
            ? [{ id: 'PETTY CASH VOUCHER', text: 'PETTY CASH VOUCHER' }]
            : [
                { id: 'PROFORMA INVOICE', text: 'PROFORMA INVOICE' },
                { id: 'TAX INVOICE', text: 'TAX INVOICE' },
              ];
        const chargesRequiringDoc = items
            .filter(item => item.requires_document)
            .map(item => {
                const charge = chargeOptions.find(c => c.id === item.charge_name);
                const docType = item.document_types || charge?.document_types || '';
                return docType ? docType.toUpperCase() : null;
            })
            .filter(Boolean);
        
        const uniqueChargeTypes = [...new Set(chargesRequiringDoc)];
        setDocumentTypeOptions([...defaultTypes, ...uniqueChargeTypes.map(name => ({ id: name, text: name }))]);
    }, [items, chargeOptions]);

    // IDF Search Debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            if (jobSearchField && jobSearchTerm && jobSearchTerm.length > 1 && !jobDetails) {
                 fetchJobDetails(jobSearchTerm, false, jobSearchField);
            }
        }, 600);
        return () => clearTimeout(timer);
    }, [jobSearchTerm, jobSearchField]);

    const fetchJobDetails = async (term, forceSelect = false, searchField = jobSearchField) => {
        setLoadingInfo(true);
        try {
            const q = encodeURIComponent(term || '');
            const sf = encodeURIComponent(searchField || 'idf_no');
            const exactParam = forceSelect ? '&exact=true' : '';
            const res = await fetch(`${API_JOB_SEARCH}?search_field=${sf}&search=${q}${exactParam}`, { headers: getAuthHeaders() });
            if (res.ok) {
                const results = await res.json();
                setIdfOptions(results);
                
                if (forceSelect && results.length > 0) {
                     handleSelectJob(results[0]);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingInfo(false);
        }
    };

    const handleSelectJob = (job) => {
        setJobDetails(job);
        setFormData(prev => ({
            ...prev,
            job: job.id
        }));
        setJobSearchTerm(getJobSearchValue(job, jobSearchField || 'idf_no'));
        setIdfOptions([]);
    };

    // Item Management
    const addItem = () => {
        setItems(prev => [...prev, { charge_name: '', amount: 0, add_vat: false, vat_amount: 0, total_amount: 0, is_cash: true, requires_document: false, units: 1, unit_type: '', unit_rate: 0 }]);
    };

    const removeItem = (index) => {
        setItems(prev => prev.filter((_, i) => i !== index));
    };

    const updateItem = (index, field, value) => {
        const newItems = [...items];

        if (field === 'charge_name') {
            newItems[index][field] = value;
            const charge = chargeOptions.find(c => c.id === value);
            if (charge) {
                newItems[index].add_vat = charge.add_vat;
                newItems[index].is_cash = charge.cash;
                newItems[index].requires_document = charge.document_required || false;
                newItems[index].document_types = charge.document_types || '';
                // Recalculate VAT if charge has VAT
                const amountNum = parseFloat(newItems[index].amount) || 0;
                if (charge.add_vat && newItems[index].amount) {
                    newItems[index].vat_amount = amountNum * 0.18; // 18% VAT
                    newItems[index].total_amount = amountNum + newItems[index].vat_amount;
                } else {
                    newItems[index].vat_amount = 0;
                    newItems[index].total_amount = amountNum;
                }
            }
        } else if (field === 'amount') {
            newItems[index][field] = value;
            // Recalculate VAT if item has VAT
            const amountNum = parseFloat(value) || 0;
            if (newItems[index].add_vat) {
                newItems[index].vat_amount = amountNum * 0.18; // 18% VAT
                newItems[index].total_amount = amountNum + newItems[index].vat_amount;
            } else {
                newItems[index].vat_amount = 0;
                newItems[index].total_amount = amountNum;
            }
        } else if (field === 'add_vat') {
            newItems[index].add_vat = value;
            if (value) {
                newItems[index].vat_amount = parseFloat(newItems[index].amount || 0) * 0.18;
            } else {
                newItems[index].vat_amount = 0;
            }
            newItems[index].total_amount = parseFloat(newItems[index].amount || 0) + newItems[index].vat_amount;
        } else if (field === 'vat_amount') {
            const parsedValue = parseFloat(value) || 0;
            newItems[index][field] = parsedValue;
            const parsedAmount = parseFloat(newItems[index].amount) || 0;
            newItems[index].total_amount = parsedAmount + parsedValue;
        } else if (field === 'total_amount') {
            const total = parseFloat(value) || 0;
            if (newItems[index].add_vat) {
                const amt = total / 1.18;
                newItems[index].amount = amt;
                newItems[index].vat_amount = total - amt;
            } else {
                newItems[index].amount = total;
                newItems[index].vat_amount = 0;
            }
            newItems[index].total_amount = total;
            newItems[index].unit_rate = newItems[index].amount;
        } else {
            newItems[index][field] = value;
        }

        setItems(newItems);
    };

    
    // Document Management
    const addDocument = () => {
        setDocuments(prev => [...prev, { type: isTransporter ? 'PETTY CASH VOUCHER' : '', file: null }]);
    };

    const removeDocument = (index) => {
        const doc = documents[index];
        if (doc && doc.id) {
            setDocIdsToDelete(prev => [...prev, doc.id]);
        }
        setDocuments(prev => prev.filter((_, i) => i !== index));
    };

    const updateDocument = (index, field, value) => {
        const newDocs = [...documents];
        newDocs[index][field] = value;
        setDocuments(newDocs);
    };

    // Staff creation for this payable only
    const handleStaffCreate = (newStaffName) => {
        const newStaff = { id: `temp_${Date.now()}`, text: newStaffName };
        setTempStaffOptions(prev => [...prev, newStaff]);
        setStaffOptions(prev => [...prev, newStaff]);
        setFormData(prev => ({ ...prev, staff: newStaff.id }));
    };

    const checkDuplication = async () => {
        // Only check for Vendor type
        if (formData.vendor_staff_type !== 'VENDOR') {
            return true;
        }

        const invoiceType = formData.invoice_type;
        const invoiceNo = formData.invoice_no;
        const totalAmount = totalPayable || 0;
        const vendor = formData.vendor;

        if (!invoiceNo || invoiceNo.trim() === '') {
            return true; // No invoice number to check
        }

        if (!vendor) {
            return true; // No vendor to check
        }

        try {
            const params = new URLSearchParams();
            if (invoiceType === 'PROFORMA') {
                params.append('proforma_invoice_no', invoiceNo);
            } else if (invoiceType === 'TAX') {
                params.append('tax_invoice_no', invoiceNo);
            }

            const res = await fetch(`${API_PAYABLES}?${params.toString()}`, { headers: getAuthHeaders() });
            if (!res.ok) return true; // If check fails, allow save

            const data = await res.json();
            const existingPayables = data.results || data;

            // Filter out the current payable if editing and only check same vendor
            const otherPayables = existingPayables.filter(p => {
                if (initialData?.id && p.id === initialData.id) return false;
                return String(p.vendor) === String(vendor);
            });

            if (otherPayables.length > 0) {
                const existing = otherPayables[0];
                const existingTotal = existing.total_amount || 0;
                const invoiceTypeName = invoiceType === 'PROFORMA' ? 'Proforma Invoice' : 'Tax Invoice';

                if (Math.abs(parseFloat(existingTotal) - parseFloat(totalAmount)) < 0.01) {
                    // Same invoice number and same total amount
                    return new Promise((resolve) => {
                        showConfirm(
                            'Duplicate Payable Warning',
                            <span>Payable Reference <strong>{existing.reference_no}</strong> exists with same {invoiceTypeName} Number <strong>{invoiceNo}</strong> and same Total Amount <strong>{totalAmount}</strong>. Are you sure you would like to continue saving this payable?</span>,
                            () => resolve(true),
                            () => resolve(false)
                        );
                    });
                } else {
                    // Same invoice number but different total amount
                    return new Promise((resolve) => {
                        showConfirm(
                            'Duplicate Payable Warning',
                            <span>Payable Reference <strong>{existing.reference_no}</strong> exists with same {invoiceTypeName} Number <strong>{invoiceNo}</strong>. Are you sure you would like to continue saving this payable?</span>,
                            () => resolve(true),
                            () => resolve(false)
                        );
                    });
                }
            }

            return true;
        } catch (error) {
            console.error('Error checking duplication:', error);
            return true; // If check fails, allow save
        }
    };

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();

        // Check for duplication first (only for Vendor type)
        const canProceed = await checkDuplication();
        if (!canProceed) return;

        // Validation
        if (!formData.job && (module || '').toUpperCase() !== 'ACCOUNTS') {
            notifyError("Please select a Job/IDF");
            return;
        }

        if (!formData.vendor_staff_type) {
            notifyError("Please select Vendor or Staff");
            return;
        }

        if (formData.vendor_staff_type === 'VENDOR' && !formData.vendor) {
            notifyError("Please select a Vendor");
            return;
        }

        if (formData.vendor_staff_type === 'VENDOR') {
            if (!formData.invoice_type) {
                notifyError("Please select an Invoice Type (Proforma or Tax Invoice)");
                return;
            }
            if (!formData.invoice_no) {
                notifyError(`Please enter the ${formData.invoice_type === 'PROFORMA' ? 'Proforma Invoice Number' : formData.invoice_type === 'VOUCHER' ? 'Voucher Number' : 'Tax Invoice Number'}`);
                return;
            }
            if (!formData.invoice_date) {
                notifyError("Please enter the Invoice Date");
                return;
            }
            const requiredDocType = formData.invoice_type === 'PROFORMA' ? 'proforma invoice' : formData.invoice_type === 'VOUCHER' ? 'petty cash voucher' : 'tax invoice';
            const hasInvoiceDoc = documents.some(d =>
                (d.file || d.existingUrl) && d.type && d.type.toLowerCase().includes(requiredDocType)
            );
            if (!hasInvoiceDoc) {
                notifyError(`Please attach a document with type "${requiredDocType.toUpperCase()}" before saving`);
                return;
            }
        }

        if (isTransporter) {
            const hasPettyCashVoucher = documents.some(d =>
                (d.file || d.existingUrl) && d.type && d.type.toLowerCase() === 'petty cash voucher'
            );
            if (!hasPettyCashVoucher) {
                notifyError('Please attach a "PETTY CASH VOUCHER" document before saving');
                return;
            }
        }

        if (formData.vendor_staff_type === 'STAFF' && !formData.staff) {
            notifyError("Please select a Staff member");
            return;
        }

        if (formData.vendor_staff_type !== 'VENDOR' && !formData.payment_date) {
            notifyError("Payment Date is required");
            return;
        }

        // Validate items
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (!item.charge_name) {
                notifyError(`Please select a Charge Name for item ${i + 1}`);
                return;
            }
            const itemAmount = parseFloat(item.amount);
            const isWht = isWhtCharge(item.charge_name);
            const isAdjustment = item.charge_name === 'ADJUSTMENT';
            if (item.amount === '' || item.amount === null || item.amount === undefined || Number.isNaN(itemAmount) || (!isWht && !isAdjustment && itemAmount <= 0)) {
                notifyError(`Please enter a valid Amount for item ${i + 1}`);
                return;
            }
        }

        // Document validation - if any selected charge has document_required, must attach a matching document
        for (const item of items) {
            const charge = chargeOptions.find(c => c.id === item.charge_name);
            const docRequired = item.requires_document || charge?.document_required || false;
            if (docRequired) {
                const requiredDocType = (item.document_types || charge?.document_types || '').toUpperCase();
                if (!requiredDocType) {
                    notifyError(`Charge "${charge?.text || item.charge_name}" requires a document but has no document type configured. Please contact admin.`);
                    return;
                }
                const hasMatchingDoc = documents.some(d =>
                    (d.file || d.existingUrl) && d.type && d.type.toUpperCase() === requiredDocType
                );
                if (!hasMatchingDoc) {
                    notifyError(`"${requiredDocType}" must be attached for charge "${charge?.text || item.charge_name}".`);
                    return;
                }
            }
        }

        // KK Seal Number validation - if job has containers and cargo type is FCL, all must have KK Seal Number
        if (jobDetails && 
            jobDetails.cargo_type && 
            jobDetails.cargo_type.toUpperCase() === 'FCL' &&
            jobDetails.containers_list && 
            Array.isArray(jobDetails.containers_list) && 
            jobDetails.containers_list.length > 0) {
            const containersWithoutSeal = jobDetails.containers_list.filter(c => !c.kk_seal_no || c.kk_seal_no.trim() === '');
            if (containersWithoutSeal.length > 0) {
                notifyError("All containers for this job must have KK Seal Number before saving");
                return;
            }
        }

        if (formData.pay_by_control_no) {
            if (!formData.control_no) { notifyError("Control Number is required"); return; }
            if (!formData.control_no_type) { notifyError("Please select GEPG or Other for the Control Number"); return; }
        } else if (formData.control_no && !formData.control_no_type) {
            notifyError("Please select GEPG or Other for the Control Number");
            return;
        }

        showConfirm(
            'Confirm Save',
            'Are you sure you have verified all details before saving?',
            () => executeSubmit()
        );
    };

    const executeSubmit = async () => {
        setSubmitting(true);
        const payload = new FormData();

        if (!initialData?.id) payload.append('job', formData.job);
        payload.append('vendor_staff_type', formData.vendor_staff_type);
        if (formData.vendor_staff_type === 'VENDOR') {
            if (isTransporter) {
                payload.append('transporter', formData.vendor);
            } else {
                payload.append('vendor', formData.vendor);
            }
        } else if (formData.vendor_staff_type === 'STAFF') {
            // staff is stored as staff_name (text) since there's no staff FK on the model
            const staffText = formData.staff_name ||
                staffOptions.find(s => String(s.id) === String(formData.staff))?.text ||
                tempStaffOptions.find(s => String(s.id) === String(formData.staff))?.text ||
                formData.staff;
            payload.append('staff_name', staffText);
        }
        if (formData.payment_date) payload.append('payment_date', formData.payment_date);
        payload.append('remarks', formData.remarks);
        payload.append('currency', formData.currency);
        payload.append('pay_by_control_no', formData.pay_by_control_no);
        
        if (receiptUploadOnly) {
            // Receipt Upload Only mode - only save invoice type, tax invoice number, invoice date and document
            if (formData.invoice_type) payload.append('invoice_type', formData.invoice_type);
            if (formData.invoice_no) {
                if (formData.invoice_type === 'PROFORMA') {
                    payload.append('proforma_invoice_no', formData.invoice_no);
                } else {
                    payload.append('tax_invoice_no', formData.invoice_no);
                }
            }
            if (formData.invoice_date) payload.append('invoice_date', formData.invoice_date);
            payload.append('receipt_uploaded', 'true');
            payload.append('receipt_approved', 'false');
            payload.append('status', 'APPROVED 3');
        } else {
            // Standard save mode
            if (formData.pay_by_control_no) {
                payload.append('control_no', formData.control_no);
                payload.append('control_no_type', formData.control_no_type);
            }
            if (formData.vendor_staff_type === 'VENDOR' && formData.invoice_type) {
                payload.append('invoice_type', formData.invoice_type);
                if (formData.invoice_date) payload.append('invoice_date', formData.invoice_date);
                if (formData.invoice_type === 'PROFORMA') {
                    if (formData.invoice_no) payload.append('proforma_invoice_no', formData.invoice_no);
                } else {
                    if (formData.invoice_no) payload.append('tax_invoice_no', formData.invoice_no);
                }
            }

            // Include module for reference number generation
            if (!initialData?.id) {
                payload.append('module', 'OFFICE');
                if (module) {
                    payload.append('origin_module', module.toUpperCase());
                }
            }

            // Add items
            const itemsWithTotals = items.map(item => ({
                ...item,
                units: 1,
                unit_rate: parseFloat(item.amount) || 0,
                vat_amount: item.add_vat ? (parseFloat(item.vat_amount) || 0).toFixed(2) : '0.00',
                total_amount: item.add_vat ? (parseFloat(item.total_amount) || parseFloat(item.amount)).toFixed(2) : item.amount
            }));
            payload.append('items', JSON.stringify(itemsWithTotals));
            
            // Send totals
            payload.append('net_amount', netAmount);
            payload.append('total_vat', totalVat);
            payload.append('total_amount', totalPayable);
        }

        // Documents
        const newDocs = documents.filter(d => d.type && d.file && !d.id);
        const docsMeta = newDocs.map(d => ({ type: d.type }));
        
        if (docsMeta.length > 0) {
            payload.append('documents_metadata', JSON.stringify(docsMeta));
            newDocs.forEach((doc) => {
                payload.append('document_files', doc.file); 
            });
        }

        if (docIdsToDelete.length > 0) {
            payload.append('document_ids_to_delete', JSON.stringify(docIdsToDelete));
        }

        const url = initialData?.id ? `${API_PAYABLES}${initialData.id}/` : API_PAYABLES;
        const method = initialData?.id ? 'PATCH' : 'POST';

        console.log('Submitting payload:');
        for (const [k, v] of payload.entries()) console.log(' ', k, '=', v);

        try {
            const res = await fetch(url, {
                method,
                headers: getAuthHeaders(true),
                body: payload
            });

            if (res.ok) {
                notifySuccess(`Cash Payable ${initialData?.id ? 'Updated' : 'Created'}`);
                if (onSave) onSave();
                onClose();
            } else {
                const err = await res.json();
                console.error('Save error response:', err);
                notifyError("Failed: " + JSON.stringify(err));
            }
        } catch (error) {
            notifyError("Error: " + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleStage2Approve = () => {
        showConfirm(
            'Approve Payable',
            'Are you sure you want to approve this payable?',
            () => executeStage2Approve()
        );
    };

    const executeStage2Approve = async () => {
        if (!stage2Data.bank) { notifyError("Please select a Bank or Cash"); return; }
        if (!stage2Data.cheque_no && stage2Data.bank !== 'CASH') { notifyError("Please enter Cheque/Ref No"); return; }
        if (!stage2Data.collected_by) { notifyError("Please enter Collected By"); return; }
        setSubmitting(true);
        const payload = new FormData();
        payload.append('bank_account', stage2Data.bank);
        payload.append('cheque_no', stage2Data.cheque_no);
        payload.append('collected_by', stage2Data.collected_by);
        payload.append('status', 'APPROVED 2');
        const newDocs = documents.filter(d => d.type && d.file && !d.id);
        if (newDocs.length > 0) {
            payload.append('documents_metadata', JSON.stringify(newDocs.map(d => ({ type: d.type }))));
            newDocs.forEach(doc => payload.append('document_files', doc.file));
        }
        try {
            const res = await fetch(`${API_PAYABLES}${initialData.id}/`, { method: 'PATCH', headers: getAuthHeaders(true), body: payload });
            if (res.ok) { notifySuccess(stage2Data.bank === 'CASH' ? "Approved & Moved to Expenses" : "Approved & Moved to Stage 3"); if (onSave) onSave(); onClose(); }
            else { const err = await res.json(); notifyError("Failed: " + JSON.stringify(err)); }
        } catch(e) { notifyError(e.message); } finally { setSubmitting(false); }
    };

    const handleStage3Approve = () => {
        showConfirm(
            'Confirm Approval',
            'Are you sure you want to approve this payable?',
            () => executeStage3Approve()
        );
    };

    const executeStage3Approve = async () => {
        if (formData.pay_by_control_no) {
            if (!stage3Data.payment_source) { notifyError("Please select a Payment Method"); return; }
        } else {
            if (!stage2Data.bank) { notifyError("Please select a Payment Method"); return; }
            if (!stage2Data.collected_by && !initialData?.is_contra) { notifyError("Please enter Collected By"); return; }
        }
        if (!stage3Data.payment_date) { notifyError("Please select a Payment Date"); return; }
        if (!isGepgPayable && !isCash && !stage3Data.tiss_tips) { setShowStage3TissError(true); notifyError("Please select TISS/TIPS option"); return; }
        setShowStage3TissError(false);
        setSubmitting(true);
        const payload = new FormData();
        payload.append('payment_date', stage3Data.payment_date);
        payload.append('tiss_tips', stage3Data.tiss_tips);
        payload.append('tiss_tips_amount', isTissOrTipsSelected ? stage3TissTipsAmount.toFixed(2) : '0');
        if (formData.pay_by_control_no) {
            payload.append('bank_account', stage3Data.payment_source);
            if (isGepgPayable) {
                payload.append('gepg_amount', gepgAmount.toFixed(2));
            }
        } else {
            payload.append('bank_account', stage2Data.bank);
            payload.append('cheque_no', stage2Data.cheque_no || '');
            payload.append('collected_by', stage2Data.collected_by || '');
            payload.append('paid_by', stage2Data.paid_by || '');
        }
        
        // Check if vendor has tax_invoice_has_receipt and tax invoice document is attached
        const hasTaxInvoiceDocument = documents.some(d => d.type === 'TAX INVOICE') ||
            (initialData.documents && initialData.documents.some(d => d.document_type === 'Tax Invoice'));
        if (initialData.vendor_tax_invoice_has_receipt && hasTaxInvoiceDocument) {
            payload.append('receipt_approved', 'true');
            payload.append('receipt_uploaded', 'true');
        }
        
        payload.append('status', 'APPROVED 3');
        const newDocs = documents.filter(d => d.type && d.file && !d.id);
        if (newDocs.length > 0) {
            payload.append('documents_metadata', JSON.stringify(newDocs.map(d => ({ type: d.type }))));
            newDocs.forEach(doc => payload.append('document_files', doc.file));
        }
        try {
            const res = await fetch(`${API_PAYABLES}${initialData.id}/`, { method: 'PATCH', headers: getAuthHeaders(true), body: payload });
            if (res.ok) {
                notifySuccess("Approved & Moved to Expenses");
                if (onSave) onSave();
                onClose();
            } else {
                const err = await res.json();
                console.error('Stage 3 approval error:', err);
                notifyError("Failed: " + JSON.stringify(err));
            }
        } catch(e) {
            console.error('Stage 3 approval exception:', e);
            notifyError(e.message);
        } finally { setSubmitting(false); }
    };

    return (
        <div
            className="dialog-container cash-payables-compact"
            style={{
                width: '95%',
                maxWidth: popoutMode ? 'none' : '1300px',
                height: popoutMode ? '95vh' : 'auto',
                maxHeight: popoutMode ? '95vh' : '60vh',
                display: 'flex',
                flexDirection: 'column',
                margin: popoutMode ? '2vh auto' : '0 auto',
                fontSize: '10px',
                fontFamily: 'Arial, sans-serif'
            }}
        >
            <style>
                {`
                    .cash-payables-compact .dialog-header { padding: 6px 10px; }
                    .cash-payables-compact .dialog-header h3 { margin: 0; font-size: 13px; font-family: Arial, sans-serif; font-weight: 600; }
                    .cash-payables-compact .dialog-body { padding: 8px !important; }
                    .cash-payables-compact .form-section { margin-bottom: 6px; }
                    .cash-payables-compact .form-section h4 { margin: 0 0 5px 0; padding-bottom: 3px; font-size: 11px; font-family: Arial, sans-serif; }
                    .cash-payables-compact label,
                    .cash-payables-compact small,
                    .cash-payables-compact .form-check-label,
                    .cash-payables-compact .text-muted { font-size: 10px; font-family: Arial, sans-serif; color: #555; margin: 2px 0 1px !important; display: block; }
                    .cash-payables-compact .form-control,
                    .cash-payables-compact input:not([type="checkbox"]):not([type="radio"]),
                    .cash-payables-compact select { height: 22px !important; font-size: 10px; font-family: Arial, sans-serif; padding: 1px 5px; }
                    .cash-payables-compact textarea.form-control { height: auto !important; min-height: 36px; font-size: 10px; font-family: Arial, sans-serif; padding: 3px 5px; }
                    .cash-payables-compact .select2-selection { min-height: 22px !important; padding: 1px 5px !important; font-size: 10px !important; }
                    .cash-payables-compact .select2-selection__rendered { font-size: 10px !important; }
                    .cash-payables-compact .select2-search { padding: 3px !important; }
                    .cash-payables-compact .select2-search__field { font-size: 10px !important; padding: 2px 4px !important; }
                    .cash-payables-compact .select2-results__option { font-size: 10px !important; padding: 4px 6px !important; }
                    .cash-payables-compact table th,
                    .cash-payables-compact table td { padding: 2px 4px !important; font-size: 10px; font-family: Arial, sans-serif; vertical-align: middle; }
                    .cash-payables-compact .btn,
                    .cash-payables-compact .dialog-btn { padding: 3px 7px; font-size: 10px; font-family: Arial, sans-serif; }
                    .cash-payables-compact .docs-table td { vertical-align: middle; }
                `}
            </style>
            <div className="dialog-header dis-flex-between">
                <div>
                   <h3>{mode === 'edit' ? 'EDIT CASH PAYABLE' : mode === 'view' ? 'VIEW CASH PAYABLE' : 'NEW CASH PAYABLE'}</h3>
                   {initialData?.reference_no && (
                       <span style={{ fontSize: '1em', color: '#ffffff', fontWeight: '600', letterSpacing: '0.05em' }}>
                           Ref: {initialData.reference_no}
                       </span>
                   )}
                </div>
            </div>
            
            <div className="dialog-body" style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
                <form onSubmit={handleSubmit} noValidate>
                    {/* Approval History */}
                    {(isView || receiptUploadOnly) && initialData && !isTransporterPortal && (() => {
                        const createdByEntry = (initialData.created_by_name || (isTransporter && initialData.transporter_name)) ? [{
                            key: 'created-by',
                            label: 'CREATED BY:',
                            by: isTransporter ? (initialData.created_by_name || initialData.transporter_name) : initialData.created_by_name,
                            at: initialData.created_at,
                            isCreated: true,
                        }] : [];
                        const approvalEntries = (initialData.approval_history?.length
                            ? initialData.approval_history
                                .filter(event => !['REJECTED', 'REJECTION'].includes(String(event.event_type || '').toUpperCase()))
                                .map((ev, idx) => ({
                                    key: `ev-${ev.id || idx}`,
                                    label: ev.event_type === 'STAGE_1_APPROVED' ? 'STAGE 1 APPROVED:'
                                        : ev.event_type === 'STAGE_2_APPROVED' ? 'STAGE 2 APPROVED:'
                                        : ev.event_type === 'STAGE_3_APPROVED' ? 'STAGE 3 APPROVED:'
                                        : ev.event_type === 'RETURNED_TO_STAGE_3' ? 'RETURNED TO STAGE 3:'
                                        : ev.event_type === 'RETURNED_TO_STAGE_2' ? 'RETURNED TO STAGE 2:'
                                        : ev.event_type === 'RETURNED_TO_STAGE_1' ? 'RETURNED TO STAGE 1:'
                                        : `${String(ev.event_type || '').replace(/_/g, ' ')}:`,
                                    by: ev.acted_by_name,
                                    at: ev.event_at,
                                }))
                            : [
                                { key: 's1', label: 'STAGE 1 APPROVED:', by: initialData.approved_stage_1_by_name, at: initialData.approved_stage_1_at },
                                { key: 's2', label: 'STAGE 2 APPROVED:', by: initialData.approved_stage_2_by_name, at: initialData.approved_stage_2_at },
                                { key: 's3', label: 'STAGE 3 APPROVED:', by: initialData.approved_stage_3_by_name, at: initialData.approved_stage_3_at },
                            ]
                        )
                            .map(e => ({ ...e, timestamp: Date.parse(e.at) }))
                            .filter(e => e.by && e.at && !Number.isNaN(e.timestamp))
                            .sort((a, b) => a.timestamp - b.timestamp);
                        const rejectionEntries = (initialData?.rejections || []).map((r, idx) => ({
                            key: `rejection-${r.id || idx}`,
                            label: 'REJECTED:',
                            by: r.rejected_by_name,
                            at: r.rejection_date,
                            reason: r.reason || r.remarks || '',
                            isRejection: true,
                            timestamp: Date.parse(r.rejection_date),
                        }));
                        const allEntries = [
                            ...createdByEntry,
                            ...[...approvalEntries, ...rejectionEntries].sort((a, b) => a.timestamp - b.timestamp)
                        ];
                        return (
                            <div style={{ marginBottom: '8px', padding: '6px', background: '#f8f9fa', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', borderBottom: '1px solid #dee2e6', marginBottom: '5px', paddingBottom: '3px' }}>
                                    <button type="button" className="icon-btn" onClick={() => setShowApprovalHistory(prev => !prev)} style={{ color: '#555' }}>
                                        <i className={`fa ${showApprovalHistory ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
                                    </button>
                                    <div style={{ fontWeight: 'bold' }}>APPROVAL HISTORY</div>
                                </div>
                                {showApprovalHistory && (
                                <>
                                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #dee2e6', marginTop: '5px' }}>
                                    <thead>
                                        <tr style={{ background: '#e9ecef' }}>
                                            <th style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>EVENT</th>
                                            <th style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>USER</th>
                                            <th style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'center', fontWeight: 'bold' }}>DATE</th>
                                            <th style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>REJECTION REASON</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allEntries.map((entry) => (
                                            <tr key={entry.key}
                                                onMouseEnter={e => e.currentTarget.style.background = '#eef2ff'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                            >
                                                <td style={{ border: '1px solid #dee2e6', padding: '6px 8px', fontWeight: 'bold', color: entry.isCreated ? '#444' : (entry.isRejection ? '#c0392b' : '#666'), whiteSpace: 'nowrap' }}>{String(entry.label).toUpperCase()}</td>
                                                <td style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>{String(entry.by).toUpperCase()}</td>
                                                <td style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'center', color: '#888', whiteSpace: 'nowrap' }}>{formatDateTimeDisplay(entry.at).toUpperCase()}</td>
                                                <td style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'left', color: 'red', whiteSpace: 'pre-wrap' }}>{(entry.reason || '').toUpperCase()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                </>
                                )}
                            </div>
                        );
                    })()}
                    {(module || '').toUpperCase() !== 'ACCOUNTS' && <div className="form-section">
                        <h4>JOB SELECTION</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
                            <div style={{ position: 'relative' }}>
                                <label className="required">SEARCH JOB</label>
                                {(mode === 'edit' || mode === 'view') ? (
                                    <div style={{ display: 'grid', gridTemplateColumns: '210px minmax(240px, 420px)', gap: '8px', alignItems: 'center' }}>
                                        <div className="form-control" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
                                            {JOB_SEARCH_FIELDS.find(f => f.id === getDefaultReadOnlySearchInfo().field)?.text || 'IDF No.'}
                                        </div>
                                        <div className="form-control" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
                                            {getDefaultReadOnlySearchInfo().value || '-'}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: '210px minmax(240px, 420px)', gap: '8px', alignItems: 'center' }}>
                                        <div>
                                            {isTransporter ? (
                                                <div className="form-control" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>IDF</div>
                                            ) : (
                                                <Select2Dropdown
                                                    options={JOB_SEARCH_FIELDS}
                                                    value={jobSearchField}
                                                    onChange={(val) => {
                                                        setJobSearchField(val);
                                                        setJobSearchTerm('');
                                                        setIdfOptions([]);
                                                        setJobDetails(null);
                                                    }}
                                                    valueKey="id"
                                                    displayKey="text"
                                                    placeholder="SELECT SEARCH FIELD"
                                                    required
                                                    isDisabled={!!jobDetails}
                                                />
                                            )}
                                        </div>
                                        <input 
                                            type="text" 
                                            value={jobSearchTerm}
                                            onChange={(e) => {
                                                setJobSearchTerm(e.target.value);
                                                setActiveDropdownIndex(-1);
                                                if(!e.target.value) setJobDetails(null);
                                            }}
                                            onKeyDown={(e) => {
                                                if (idfOptions.length === 0) return;
                                                if (e.key === 'ArrowDown') {
                                                    e.preventDefault();
                                                    setActiveDropdownIndex(prev => Math.min(prev + 1, idfOptions.length - 1));
                                                } else if (e.key === 'ArrowUp') {
                                                    e.preventDefault();
                                                    setActiveDropdownIndex(prev => Math.max(prev - 1, 0));
                                                } else if (e.key === 'Enter') {
                                                    if (activeDropdownIndex >= 0 && activeDropdownIndex < idfOptions.length) {
                                                        e.preventDefault();
                                                        handleSelectJob(idfOptions[activeDropdownIndex]);
                                                        setActiveDropdownIndex(-1);
                                                    }
                                                } else if (e.key === 'Escape') {
                                                    setIdfOptions([]);
                                                    setActiveDropdownIndex(-1);
                                                }
                                            }}
                                            placeholder={jobSearchField ? `TYPE TO SEARCH ${JOB_SEARCH_FIELDS.find(f => f.id === jobSearchField)?.text?.toUpperCase() || 'VALUE'}...` : 'SELECT SEARCH FIELD FIRST...'}
                                            disabled={!jobSearchField || !!jobDetails}
                                            required
                                            className="form-control"
                                        />
                                    </div>
                                )}
                                {idfOptions.length > 0 && !jobDetails && (
                                    <div className="dropdown-results" style={{
                                        position: 'absolute', left: '218px', width: 'min(420px, calc(100% - 218px))', zIndex: 1000,
                                        background: 'white', border: '1px solid #ccc', maxHeight: '200px', overflowY: 'auto',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                                    }}>
                                        {idfOptions.map((opt, idx) => (
                                            <div key={opt.id}
                                                onClick={() => { handleSelectJob(opt); setActiveDropdownIndex(-1); }}
                                                onMouseEnter={() => setActiveDropdownIndex(idx)}
                                                onMouseLeave={() => setActiveDropdownIndex(-1)}
                                                style={{
                                                    padding: '6px 10px',
                                                    cursor: 'pointer',
                                                    backgroundColor: idx === activeDropdownIndex ? '#0d6efd' : 'white',
                                                    color: idx === activeDropdownIndex ? '#fff' : '#212529',
                                                    transition: 'background-color 0.1s'
                                                }}
                                            >
                                                <strong>{getJobSearchValue(opt, jobSearchField || 'idf_no')}</strong> - {opt.customer}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        {jobDetails && (
                            <div className="form-section" style={{ marginTop: '8px', backgroundColor: '#e8f5e9', padding: '8px 12px', borderRadius: '5px', border: '1px solid #c8e6c9' }}>
                            <h4 style={{ color: '#2e7d32', borderBottom: '1px solid #a5d6a7', paddingBottom: '5px' }}>JOB DETAILS</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                                <div>
                                    <small style={{ color: '#666' }}>JOB ID</small>
                                    <div style={{ fontWeight: 'bold' }}>{jobDetails.id}</div>
                                </div>
                                <div>
                                    <small style={{ color: '#666' }}>FILE NO</small>
                                    <div style={{ fontWeight: 'bold' }}>{jobDetails.kk_file_no}</div>
                                </div>
                                <div>
                                    <small style={{ color: '#666' }}>IDF NO</small>
                                    <div style={{ fontWeight: 'bold' }}>{jobDetails.idf_no}</div>
                                </div>
                                <div>
                                    <small style={{ color: '#666' }}>CUSTOMER</small>
                                    <div style={{ fontWeight: 'bold' }}>{jobDetails.customer}</div>
                                </div>
                                <div>
                                    {jobDetails.customer_tin && (<>
                                        <small style={{ color: '#666' }}>CUSTOMER TIN</small>
                                        <div style={{ fontWeight: 'bold' }}>{jobDetails.customer_tin}</div>
                                    </>)}
                                </div>                                
                                <div>
                                    <small style={{ color: '#666' }}>BL NUMBER</small>
                                    <div style={{ fontWeight: 'bold' }}>{jobDetails.bl_no}</div>
                                </div>
                                <div style={{ gridColumn: 'span 3' }}>
                                    <small style={{ color: '#666' }}>CONTAINERS</small>
                                    <div style={{ fontWeight: 'bold' }}>
                                        {jobDetails.container_summary}
                                        {(() => {
                                            const containerList = isTransporter && initialData?.containers?.length
                                                ? initialData.containers
                                                : (jobDetails?.containers_list?.length ? jobDetails.containers_list : (jobDetails?.containers || []));
                                            const containerListString = containerList
                                                .map(c => c.container_no || c.number || '')
                                                .filter(Boolean)
                                                .join(' | ');
                                            return containerListString ? <span style={{ color: '#c0392b', marginLeft: 10 }}>| {containerListString}</span> : null;
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}
                    </div>}

                    {/* PAYMENT DETAILS */}
                    <div className="form-section" style={{ marginTop: '20px' }}>
                        <h4>VENDOR/STAFF DETAILS</h4>
                        {displayAsView ? (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                                <div>
                                    <small style={{ color: '#666' }}>VENDOR/STAFF TYPE</small>
                                    <div style={{ fontWeight: 'bold' }}>{formData.vendor_staff_type || '-'}</div>
                                </div>
                                <div>
                                    <small style={{ color: '#666' }}>{formData.vendor_staff_type === 'STAFF' ? 'STAFF' : 'VENDOR'}</small>
                                    <div style={{ fontWeight: 'bold' }}>
                                        {formData.vendor_staff_type === 'VENDOR'
                                            ? (initialData?.vendor_name || initialData?.vendor_short_name || vendorOptions.find(v => String(v.id) === String(formData.vendor))?.text || '-')
                                            : formData.vendor_staff_type === 'STAFF'
                                            ? (formData.staff_name || initialData?.staff_name || '-')
                                            : '-'}
                                    </div>
                                </div>
                                <div>
                                    <small style={{ color: '#666' }}>PAYMENT DATE</small>
                                    <div style={{ fontWeight: 'bold' }}>{formData.payment_date || '-'}</div>
                                </div>
                                <div>
                                    <small style={{ color: '#666' }}>REMARKS</small>
                                    <div style={{ fontWeight: 'bold' }}>{formData.remarks || '-'}</div>
                                </div>
                                {formData.vendor_staff_type === 'VENDOR' && formData.invoice_type && !receiptUploadOnly && (
                                    <>
                                        <div>
                                            <small style={{ color: '#666' }}>INVOICE TYPE</small>
                                            <div style={{ fontWeight: 'bold' }}>{formData.invoice_type === 'PROFORMA' ? 'PROFORMA INVOICE' : 'TAX INVOICE'}</div>
                                        </div>
                                        <div>
                                            <small style={{ color: '#666' }}>{formData.invoice_type === 'PROFORMA' ? 'PROFORMA INV NO.' : 'TAX INV NO.'}</small>
                                            <div style={{ fontWeight: 'bold' }}>{formData.invoice_no || '-'}</div>
                                        </div>
                                        <div>
                                            <small style={{ color: '#666' }}>INVOICE DATE</small>
                                            <div style={{ fontWeight: 'bold' }}>{formatDateDisplay(formData.invoice_date) || '-'}</div>
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : isTransporter ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                            <div>
                                <label>VENDOR</label>
                                <div className="form-control" style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
                                    {initialData?.vendor_name || initialData?.transporter_name || '-'}
                                </div>
                            </div>
                            <div>
                                <label>REMARKS</label>
                                <input 
                                    className="form-control"
                                    value={formData.remarks}
                                    onChange={e => setFormData({...formData, remarks: e.target.value.toUpperCase()})}
                                    disabled={isFieldDisabled}
                                />
                            </div>
                        </div>
                        ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                            <div>
                                <label className="required">VENDOR/STAFF</label>
                                <Select2Dropdown
                                    options={VENDOR_STAFF_OPTIONS}
                                    value={formData.vendor_staff_type}
                                    onChange={(val) => setFormData({ ...formData, vendor_staff_type: val, vendor: '', staff: '', pay_by_control_no: false, control_no: '', control_no_type: '', invoice_type: '', invoice_no: '', invoice_date: '' })}
                                    valueKey="value"
                                    displayKey="label"
                                    isDisabled={isFieldDisabled}
                                    required
                                    placeholder="SELECT TYPE"
                                />
                            </div>
                            <div>
                                <label className="required">
                                    {formData.vendor_staff_type === 'VENDOR' ? 'VENDOR' : formData.vendor_staff_type === 'STAFF' ? 'STAFF' : 'SELECT TYPE FIRST'}
                                </label>
                                {formData.vendor_staff_type === 'VENDOR' && (
                                    <div style={{ display: 'flex', alignItems: 'stretch', gap: '4px' }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <Select2Dropdown
                                                options={vendorOptions}
                                                value={formData.vendor}
                                                onChange={(val) => setFormData({ ...formData, vendor: val })}
                                                valueKey="id"
                                                displayKey="text"
                                                isDisabled={isFieldDisabled}
                                                required
                                                placeholder="SELECT VENDOR"
                                            />
                                        </div>
                                        {canCreateVendor && (
                                            <button
                                                type="button"
                                                onClick={() => setShowVendorForm(true)}
                                                title="Add New Vendor"
                                                style={{
                                                    padding: '0 10px',
                                                    background: '#28a745',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '16px',
                                                    fontWeight: 'bold',
                                                    lineHeight: 1,
                                                }}
                                            >+</button>
                                        )}
                                    </div>
                                )}
                                {formData.vendor_staff_type === 'STAFF' && (
                                    <Select2Dropdown
                                        options={staffOptions}
                                        value={formData.staff}
                                        onChange={(val) => {
                                            if (val === 'TEMP') {
                                                setFormData({ ...formData, staff: 'TEMP', staff_name: 'TEMP' });
                                            } else {
                                                const selected = staffOptions.find(s => String(s.id) === String(val)) ||
                                                                 tempStaffOptions.find(s => String(s.id) === String(val));
                                                setFormData({ ...formData, staff: val, staff_name: selected?.text || val });
                                                setTempStaffName(''); // Clear temp staff name when not TEMP
                                            }
                                        }}
                                        valueKey="id"
                                        displayKey="text"
                                        isDisabled={isFieldDisabled}
                                        required
                                        placeholder="SELECT STAFF"
                                        allowCreate={true}
                                        onCreate={handleStaffCreate}
                                    />
                                )}
                                {!formData.vendor_staff_type && (
                                    <div className="form-control" style={{ backgroundColor: '#e9ecef' }}>-</div>
                                )}
                                {formData.vendor_staff_type === 'STAFF' && formData.staff === 'TEMP' && (
                                    <div style={{ marginTop: '10px' }}>
                                        <label className="required">TEMP STAFF NAME</label>
                                        <input 
                                            type="text" 
                                            className="form-control"
                                            value={tempStaffName}
                                            onChange={e => {
                                                const upperValue = e.target.value.toUpperCase();
                                                setTempStaffName(upperValue);
                                                setFormData({ ...formData, staff_name: upperValue ? upperValue : 'TEMP' });
                                            }}
                                            placeholder="ENTER STAFF NAME"
                                            disabled={isFieldDisabled}
                                            required
                                        />
                                    </div>
                                )}
                            </div>
                            {formData.vendor_staff_type !== 'VENDOR' && (
                            <div>
                                <label className="required">PAYMENT DATE</label>
                                <input 
                                    type="date" 
                                    max={new Date().toISOString().split('T')[0]}
                                    className="form-control"
                                    value={formData.payment_date} 
                                    onChange={e => setFormData({...formData, payment_date: e.target.value})} 
                                    disabled={isFieldDisabled} 
                                    required
                                />
                            </div>
                            )}
                            <div>
                                <label>REMARKS</label>
                                <input 
                                    className="form-control"
                                    value={formData.remarks}
                                    onChange={e => setFormData({...formData, remarks: e.target.value.toUpperCase()})}
                                    disabled={isFieldDisabled}
                                />
                            </div>
                        </div>
                        )}
                        {formData.vendor_staff_type === 'VENDOR' && (!isView || receiptUploadOnly) && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 8 }}>
                            <div>
                                <label className="required">INVOICE TYPE</label>
                                <Select2Dropdown
                                    options={isTransporter ? [{ value: 'VOUCHER', label: 'PETTY CASH VOUCHER' }] : [{ value: 'PROFORMA', label: 'PROFORMA INVOICE' }, { value: 'TAX', label: 'TAX INVOICE' }]}
                                    value={formData.invoice_type || (isTransporter ? 'VOUCHER' : '')}
                                    onChange={val => setFormData({ ...formData, invoice_type: val, invoice_no: '' })}
                                    valueKey="value"
                                    displayKey="label"
                                    isDisabled={isTransporter || (isView && !receiptUploadOnly)}
                                    placeholder="SELECT INVOICE TYPE"
                                />
                            </div>
                            {formData.invoice_type && (
                                <>
                                    <div>
                                        <label className="required">{formData.invoice_type === 'PROFORMA' ? 'PROFORMA INVOICE NO.' : formData.invoice_type === 'VOUCHER' ? 'VOUCHER NO.' : 'TAX INVOICE NO.'}</label>
                                        {receiptUploadOnly ? (
                                            <input
                                                className="form-control"
                                                value={formData.invoice_no}
                                                onChange={e => setFormData({ ...formData, invoice_no: e.target.value.toUpperCase() })}
                                                placeholder={formData.invoice_type === 'PROFORMA' ? 'ENTER PROFORMA NO.' : formData.invoice_type === 'VOUCHER' ? 'ENTER VOUCHER NO.' : 'ENTER TAX INV NO.'}
                                                disabled={isView}
                                                style={{ textTransform: 'uppercase' }}
                                            />
                                        ) : (
                                            <input
                                                className="form-control"
                                                value={formData.invoice_no}
                                                onChange={e => setFormData({ ...formData, invoice_no: e.target.value.toUpperCase() })}
                                                placeholder={formData.invoice_type === 'PROFORMA' ? 'ENTER PROFORMA NO.' : formData.invoice_type === 'VOUCHER' ? 'ENTER VOUCHER NO.' : 'ENTER TAX INV NO.'}
                                                disabled={isFieldDisabled}
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="required">INVOICE DATE</label>
                                        {receiptUploadOnly ? (
                                            <input
                                                type="date"
                                                max={new Date().toISOString().split('T')[0]}
                                                className="form-control"
                                                value={formData.invoice_date}
                                                onChange={e => setFormData({ ...formData, invoice_date: e.target.value })}
                                                disabled={isView}
                                                required
                                            />
                                        ) : (
                                            <input
                                                type="date"
                                                max={new Date().toISOString().split('T')[0]}
                                                className="form-control"
                                                value={formData.invoice_date}
                                                onChange={e => setFormData({ ...formData, invoice_date: e.target.value })}
                                                disabled={isFieldDisabled}
                                                required
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                        )}
                    </div>
                    {formData.pay_by_control_no && (
                        (displayAsView || isStage1Approval || isStage2Approval) ? (
                            !isGepgPayable ? (
                                <div style={{ padding: '15px', background: '#e9ecef', borderRadius: '4px', marginBottom: '10px', marginTop: '10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                                        <div>
                                            <label style={{ display: 'block', fontWeight: 'bold', fontSize: '11px', color: '#888' }}>CONTROL NUMBER</label>
                                            <div style={{ fontWeight: 'bold', color: 'red', fontSize: '16px' }}>{formData.control_no}</div>
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontWeight: 'bold', fontSize: '11px', color: '#888' }}>CONTROL NUMBER TYPE</label>
                                            <div style={{ fontWeight: 'bold' }}>{formData.control_no_type || '-'}</div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div></div>
                            )
                        ) : (
                        <div style={{ padding: '15px', background: '#e9ecef', borderRadius: '4px', marginBottom: '10px', marginTop: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 1 300px' }}>
                                    <label style={{ whiteSpace: 'nowrap', marginBottom: 0 }}>CONTROL NUMBER:</label>
                                    <input
                                        className="form-control"
                                        value={formData.control_no}
                                        onChange={e => setFormData({...formData, control_no: e.target.value})}
                                        style={{ minWidth: '200px' }}
                                    />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <label style={{ marginBottom: 0, fontWeight: 'bold' }}>TYPE:</label>
                                    <div className="form-check" style={{ marginBottom: 0 }}>
                                        <input
                                            className="form-check-input" type="radio" name="officeControlType" id="officeTypeGepg"
                                            checked={formData.control_no_type === 'GEPG'}
                                            onChange={() => setFormData({...formData, control_no_type: 'GEPG'})}
                                        />
                                        <label className="form-check-label" htmlFor="officeTypeGepg" style={{ marginLeft: '5px' }}>GEPG</label>
                                    </div>
                                    <div className="form-check" style={{ marginBottom: 0 }}>
                                        <input
                                            className="form-check-input" type="radio" name="officeControlType" id="officeTypeOther"
                                            checked={formData.control_no_type === 'OTHER'}
                                            onChange={() => setFormData({...formData, control_no_type: 'OTHER'})}
                                        />
                                        <label className="form-check-label" htmlFor="officeTypeOther" style={{ marginLeft: '5px' }}>OTHER</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        )
                    )}
                    {!isView && !receiptUploadOnly && (() => {
                        const selectedVendor = vendorOptions.find(v => String(v.id) === String(formData.vendor));
                        const vendorHasControlNo = selectedVendor
                            ? (selectedVendor.payment_modes || '').toUpperCase().split(',').map(m => m.trim()).includes('CONTROL NUMBER')
                            : false;
                        return (vendorHasControlNo || formData.pay_by_control_no) && !formData.pay_by_control_no;
                    })() && (
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '10px', marginTop: '10px' }}>
                        <input
                            type="checkbox"
                            id="payByControlNo"
                            checked={formData.pay_by_control_no}
                            onChange={e => setFormData({...formData, pay_by_control_no: e.target.checked})}
                            style={{ transform: 'scale(1.2)', marginRight: '10px' }}
                        />
                        <label style={{ fontWeight: 'bold', marginBottom: 0, cursor: 'pointer' }} htmlFor="payByControlNo">PAY BY CONTROL NO.</label>
                    </div>
                    )}
                    {/* Payment Processing Section */}
                    {showPaymentSection && (
                    <div className="form-section" style={{ border: '1px solid #007bff', background: '#f0f7ff', padding: '0 8px 8px 8px', marginTop: 8 }}>
                        <h4 style={{ color: '#007bff', marginTop: 0 }}>{isReadOnlyPayment && !isStage2Approval ? 'PAYMENT DETAILS' : 'PAYMENT PROCESSING'}</h4>
                        
                        {/* Control Number View (Special Layout) */}
                        {formData.pay_by_control_no && (isStage3Approval || isExpensesView) ? (
                            isExpensesView ? (
                                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '4px 8px', fontWeight: 'bold' }}>
                                    <div>
                                        <label style={{ color: '#888', fontSize: '11px', display: 'block' }}>CONTROL NUMBER</label>
                                        <div style={{ fontSize: '16px', color: 'red' }}>{formData.control_no}</div>
                                    </div>
                                    <div>
                                        <label style={{ color: '#888', fontSize: '11px', display: 'block' }}>PAID FROM</label>
                                        <div>
                                            {[{id:'CASH', text:'CASH'}, ...filteredBankOptions].find(b => b.id === (stage3Data.payment_source || initialData?.bank_account))?.text || stage3Data.payment_source || initialData?.bank_account || '-'}
                                        </div>
                                    </div>
                                    <div>
                                        <label style={{ color: '#888', fontSize: '11px', display: 'block' }}>PAYMENT DATE</label>
                                        <div>{formatDateDisplay(stage3Data.payment_date)}</div>
                                    </div>
                                    <div>
                                        <label style={{ color: '#888', fontSize: '11px', display: 'block' }}>TISS / TIPS</label>
                                        <div>{stage3Data.tiss_tips || '-'}</div>
                                    </div>
                                    {isTissOrTipsSelected && (
                                        <div>
                                            <label style={{ color: '#888', fontSize: '11px', display: 'block' }}>TISS/TIPS AMOUNT (TZS)</label>
                                            <div style={{ color: '#c0392b' }}>{formatCurrency(parseFloat(initialData?.tiss_tips_amount || 0))}</div>
                                        </div>
                                    )}
                                    {isGepgPayable && parseFloat(initialData?.gepg_amount || 0) > 0 && (
                                        <div>
                                            <label style={{ color: '#888', fontSize: '11px', display: 'block' }}>GEPG CHARGE (TZS)</label>
                                            <div style={{ color: '#c0392b' }}>{formatCurrency(parseFloat(initialData?.gepg_amount || 0))}</div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                                    <div>
                                        <label style={{ fontWeight: 'bold' }}>CONTROL NUMBER</label>
                                        <div style={{ fontWeight: 'bold', padding: '6px 0', fontSize: '16px', color: 'red' }}>{formData.control_no}</div>
                                    </div>
                                    <div>
                                        <label style={{ fontWeight: 'bold' }}>PAID FROM</label>
                                        <Select2Dropdown
                                            options={[{ id: 'CASH', text: 'CASH' }, ...filteredBankOptions]}
                                            value={stage3Data.payment_source || initialData?.bank_account || ''}
                                            onChange={(val) => setStage3Data({...stage3Data, payment_source: val})}
                                            valueKey="id"
                                            displayKey="text"
                                            placeholder="SELECT SOURCE"
                                            menuPlacement="auto"
                                        />
                                    </div>
                                </div>
                            )
                        ) : (
                        // Regular Payment Layout
                        <>
                        {isReadOnlyPayment ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontWeight: 'bold' }}>
                                <div><small style={{ color: '#888', fontSize: '11px' }}>METHOD:</small><div>{[{id:'CASH',text:'CASH'},...bankOptions].find(b => String(b.id) === String(stage2Data.bank))?.text || stage2Data.bank || '-'}</div></div>
                                {stage2Data.cheque_no && <div><small style={{ color: '#888', fontSize: '11px' }}>CHEQUE/REF:</small><div style={{ color: 'red', fontSize: '16px' }}>{stage2Data.cheque_no}</div></div>}
                                {stage2Data.collected_by && <div><small style={{ color: '#888', fontSize: '11px' }}>COLLECTED BY:</small><div>{stage2Data.collected_by}</div></div>}
                                {stage2Data.paid_by && <div><small style={{ color: '#888', fontSize: '11px' }}>PAID BY:</small><div>{stage2Data.paid_by}</div></div>}
                                {isExpensesView && stage3Data.payment_date && <div><small style={{ color: '#888', fontSize: '11px' }}>PAYMENT DATE:</small><div>{formatDateDisplay(stage3Data.payment_date)}</div></div>}
                                {isExpensesView && stage3Data.tiss_tips && <div><small style={{ color: '#888', fontSize: '11px' }}>TISS/TIPS:</small><div>{stage3Data.tiss_tips}</div></div>}
                                {isExpensesView && isTissOrTipsSelected && <div><small style={{ color: '#888', fontSize: '11px' }}>TISS/TIPS AMOUNT:</small><div style={{ color: '#c0392b' }}>TZS {formatCurrency(parseFloat(initialData?.tiss_tips_amount || 0))}</div></div>}
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: '8px', alignItems: 'end' }}>
                                <div>
                                    <label className="required">PAYMENT METHOD (BANK/CASH)</label>
                                    <Select2Dropdown
                                        options={[{ id: 'CASH', text: 'CASH', currency: 'TZS' }, ...filteredBankOptions]}
                                        value={stage2Data.collected_by ? stage2Data.bank : (stage2Data.bank ? stage2Data.bank : null)}
                                        onChange={val => setStage2Data(prev => ({ ...prev, bank: val, cheque_no: val === 'CASH' ? '' : prev.cheque_no }))}
                                        valueKey="id"
                                        displayKey="text"
                                        placeholder="SELECT METHOD"
                                    />
                                </div>
                                {stage2Data.bank !== 'CASH' && (
                                    <div>
                                        <label className="required">CHEQUE / REF NO.</label>
                                        <input className="form-control" value={stage2Data.cheque_no} onChange={e => setStage2Data({ ...stage2Data, cheque_no: e.target.value })} placeholder="ENTER CHEQUE NO" maxLength={200} />
                                    </div>
                                )}
                                <div>
                                    <label className="required">COLLECTED BY / PAID TO</label>
                                    <input className="form-control" value={stage2Data.collected_by} onChange={e => setStage2Data({ ...stage2Data, collected_by: e.target.value.toUpperCase() })} placeholder="ENTER NAME" />
                                </div>
                            </div>
                        )}
                        </>
                        )}
                    </div>
                    )}

                    {/* PAYABLE ITEMS AND DOCUMENTS */}
                    <div style={{ marginTop: '20px' }}>
                        {/* PAYABLE ITEMS */}
                        <div className="form-section">
                            <h4>PAYABLE ITEMS</h4>
                            <table className="table table-bordered table-sm docs-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                            <thead>
                                <tr>
                                    <th style={{width: '220px'}}>CHARGE NAME</th>
                                    <th style={{width: '90px', textAlign: 'right'}}>AMOUNT (TZS)</th>
                                    {formData.vendor_staff_type === 'VENDOR' && <th style={{width: '60px', textAlign: 'center'}}>VAT</th>}
                                    {formData.vendor_staff_type === 'VENDOR' && <th style={{width: '90px', textAlign: 'right'}}>VAT AMT</th>}
                                    {formData.vendor_staff_type === 'VENDOR' && <th style={{width: '90px', textAlign: 'right'}}>TOTAL</th>}
                                    {!isFieldDisabled && <th style={{width: '30px'}}></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            {displayAsView ? (
                                                <span style={{ fontWeight: 'bold', fontSize: '12px' }}>{item.charge_name || '-'}</span>
                                            ) : (
                                                <Select2Dropdown
                                                    options={chargeOptions}
                                                    value={item.charge_name}
                                                    onChange={val => updateItem(idx, 'charge_name', val)}
                                                    valueKey="id"
                                                    displayKey="text"
                                                    isDisabled={isFieldDisabled}
                                                    placeholder="CHARGE"
                                                    style={{ minWidth: 0 }}
                                                />
                                            )}
                                            {item.requires_document && item.document_types && (
                                                <div style={{ fontSize: '10px', color: '#c0392b', fontWeight: 'bold', marginTop: '2px' }}>
                                                    <i className="fa fa-paperclip" style={{ marginRight: 3 }}></i>
                                                    Required: {item.document_types.toUpperCase()}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ width: '90px', textAlign: 'right' }}>
                                            {displayAsView ? (
                                                <span style={{ fontWeight: 'bold', fontSize: '12px' }}>{formatCurrency(item.amount)}</span>
                                            ) : (
                                                <FormattedNumberInput
                                                    value={Math.round((parseFloat(item.amount) || 0) * 100) / 100}
                                                    onChange={val => updateItem(idx, 'amount', val)}
                                                    disabled={isFieldDisabled || isTransporter}
                                                    style={{ textAlign: 'right', width: '100%', minWidth: 0 }}
                                                    required
                                                    allowNegative={isWhtCharge(item.charge_name) || item.charge_name === 'ADJUSTMENT'}
                                                />
                                            )}
                                        </td>
                                        {formData.vendor_staff_type === 'VENDOR' && (
                                            <td style={{ width: '60px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                {displayAsView ? (
                                                    item.add_vat
                                                        ? <i className="fa fa-check" style={{ color: 'green' }}></i>
                                                        : <i className="fa fa-times" style={{ color: '#ccc' }}></i>
                                                ) : (
                                                    <input
                                                        type="checkbox"
                                                        checked={!!item.add_vat}
                                                        onChange={e => updateItem(idx, 'add_vat', e.target.checked)}
                                                        disabled={isFieldDisabled || item.charge_name === 'ADJUSTMENT'}
                                                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                                    />
                                                )}
                                            </td>
                                        )}
                                        {formData.vendor_staff_type === 'VENDOR' && (
                                            <td style={{ width: '80px', maxWidth: '80px', textAlign: 'right' }}>
                                                {displayAsView ? (
                                                    <span style={{ fontWeight: 'bold', fontSize: '12px' }}>{formatCurrency(item.vat_amount)}</span>
                                                ) : (
                                                    <FormattedNumberInput
                                                        value={Math.round((item.vat_amount || 0) * 100) / 100}
                                                        onChange={val => updateItem(idx, 'vat_amount', val)}
                                                        disabled={isFieldDisabled || !item.add_vat || isTransporter}
                                                        style={{ textAlign: 'right', width: '80px', minWidth: 0, backgroundColor: item.add_vat ? undefined : '#f5f5f5' }}
                                                    />
                                                )}
                                            </td>
                                        )}
                                        {formData.vendor_staff_type === 'VENDOR' && (
                                            <td style={{ width: '80px', maxWidth: '80px', textAlign: 'right' }}>
                                                {displayAsView ? (
                                                    <span style={{ fontWeight: 'bold', fontSize: '12px' }}>{formatCurrency(item.total_amount)}</span>
                                                ) : (
                                                    <FormattedNumberInput
                                                        value={Math.round((item.total_amount || 0) * 100) / 100}
                                                        onChange={isTransporter ? val => updateItem(idx, 'total_amount', val) : undefined}
                                                        disabled={isFieldDisabled || !isTransporter}
                                                        style={{ textAlign: 'right', width: '80px', minWidth: 0, backgroundColor: isTransporter ? undefined : '#f5f5f5' }}
                                                    />
                                                )}
                                            </td>
                                        )}
                                        {!isFieldDisabled && <td style={{ width: '10px', maxWidth: '10px' }}><button type="button" className="btn btn-sm btn-danger" style={{ padding: '2px 5px', fontSize: '12px' }} onClick={() => removeItem(idx)}>&times;</button></td>}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                {!isFieldDisabled && (
                                    <tr>
                                        <td colSpan={formData.vendor_staff_type === 'VENDOR' ? 5 : 3}>
                                            <button type="button" className="btn btn-sm btn-primary" onClick={addItem}>+ ADD LINE</button>
                                        </td>
                                    </tr>
                                )}
                            </tfoot>
                            </table>

                            {/* TOTAL PAYABLE */}
                            <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'row', gap: '6px', justifyContent: 'flex-end' }}>
                                {items.some(item => item.add_vat) && (
                                    <>
                                        <div style={{ background: '#f8f9fa', padding: '5px 10px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                                            <div style={{ fontSize: '10px', color: '#495057' }}>TOTAL NET</div>
                                            <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#495057' }}>TZS {formatCurrency(netAmount)}</div>
                                        </div>
                                        <div style={{ background: '#f8f9fa', padding: '5px 10px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                                            <div style={{ fontSize: '10px', color: '#495057' }}>TOTAL VAT</div>
                                            <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#495057' }}>TZS {formatCurrency(totalVat)}</div>
                                        </div>
                                    </>
                                )}
                                <div style={{ background: '#e8f0fe', padding: '5px 10px', border: '1px solid #b8daff', textAlign: 'center' }}>
                                    <div style={{ fontSize: '10px', color: '#004085' }}>TOTAL PAYABLE</div>
                                    <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#004085' }}>TZS {formatCurrency(totalPayable)}</div>
                                </div>
                            </div>
                        </div>

                        {/* DOCUMENTS */}
                        <div className="form-section" style={{ marginTop: '20px' }}>
                        <h4>DOCUMENTS</h4>
                        <table className="table table-sm" style={{ fontSize: '12px', marginBottom: 0 }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <th style={{ textAlign: 'left', border: 'none', paddingBottom: '4px', width: '1%', whiteSpace: 'nowrap' }}>DOCUMENT TYPE</th>
                                    <th style={{ textAlign: 'left', border: 'none', paddingBottom: '4px' }}>VIEW</th>
                                    {(!isFieldDisabled || receiptUploadOnly) && <th style={{ width: '30px', border: 'none' }}></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {documents.map((doc, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        <td style={{ padding: '3px 6px', verticalAlign: 'middle', border: 'none', width: '1%', whiteSpace: 'nowrap' }}>
                                            {doc.id || doc.existingUrl || isView ? (
                                                <strong style={{ fontSize: '12px' }}>{doc.type}</strong>
                                            ) : (
                                                <div style={{ minWidth: 180 }}>
                                                    <Select2Dropdown
                                                        options={documentTypeOptions}
                                                        value={doc.type}
                                                        onChange={val => updateDocument(idx, 'type', val)}
                                                        valueKey="id"
                                                        displayKey="text"
                                                        placeholder="SELECT TYPE"
                                                    />
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ padding: '3px 6px', verticalAlign: 'middle', border: 'none' }}>
                                            {doc.existingUrl ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.preventDefault(); openFilePreview(doc.existingUrl, doc.type); }}
                                                        style={{ fontSize: 12, background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                        title="View Document"
                                                    >
                                                        View
                                                    </button>
                                                </div>
                                            ) : (
                                                !isView || receiptUploadOnly ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <input 
                                                            type="file" 
                                                            className="form-control-file" 
                                                            onChange={e => updateDocument(idx, 'file', e.target.files[0])} 
                                                            style={{ fontSize: '12px', maxWidth: '160px' }}
                                                        />
                                                        {doc.file && (
                                                            <button 
                                                                type="button"
                                                                onClick={() => { const url = URL.createObjectURL(doc.file); openFilePreview(url, doc.file.name); }}
                                                                style={{ fontSize: 12, background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                            >
                                                                View
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : <span className="text-muted" style={{ fontSize: '12px' }}>No File</span>
                                            )}
                                        </td>
                                        {(!isFieldDisabled || receiptUploadOnly) && (
                                            <td style={{ padding: '3px 4px', verticalAlign: 'middle', border: 'none', textAlign: 'center' }}>
                                                {!doc.id && !doc.existingUrl && (
                                                    <button type="button" className="btn btn-sm btn-danger" onClick={() => removeDocument(idx)}>&times;</button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                            {(!isFieldDisabled || receiptUploadOnly) && (
                                <tfoot>
                                    <tr>
                                        <td colSpan="3" style={{ border: 'none', paddingTop: '4px' }}><button type="button" className="btn btn-sm btn-secondary" onClick={addDocument}>ADD DOCUMENT</button></td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                        </div>
                    </div>                   
                </form>
            </div>

            <div className="dialog-footer">
                {receiptUploadOnly ? (
                    <>
                        <button className="dialog-btn btn-primary" onClick={handleSubmit} disabled={submitting}>SAVE PAYABLE</button>
                        <button className="dialog-btn btn-secondary" onClick={onClose}>CLOSE</button>
                    </>
                ) : (isStage2Approval || isStage3Approval) ? (
                    showRejectInput ? (
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
                            <div style={{ flex: 1 }}>
                                <small style={{ fontWeight: 'bold' }}>REASON:</small>
                                <input type="text" className="form-control" placeholder="Enter Rejection Reason..." value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
                            </div>
                            <div style={{ minWidth: '220px' }}>
                                <small style={{ fontWeight: 'bold', fontSize: '9px' }}>SEND TO:</small>
                                <Select2Dropdown
                                    options={getRejectionOptions()}
                                    value={rejectionTarget}
                                    onChange={setRejectionTarget}
                                    valueKey="id"
                                    displayKey="text"
                                    placeholder="SELECT TARGET"
                                    menuPlacement="top"
                                    popoutMode={popoutMode}
                                />
                            </div>
                            <div>
                                <button className="btn btn-danger" onClick={executeRejectionSubmit} disabled={actionLoading} style={{ marginRight: '5px' }}>Confirm Reject</button>
                                <button className="btn btn-secondary" onClick={() => { setShowRejectInput(false); setRejectionTarget(null); }}>Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {isStage3Approval && (
                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', marginRight: 'auto', flexWrap: 'nowrap' }}>
                                    <div style={{ minWidth: '200px' }}>
                                        {!isGepgPayable && !isCash && (
                                            <small style={{ fontWeight: 'bold', fontSize: '9px' }}>TISS / TIPS</small>
                                        )}
                                        {isGepgPayable || isCash ? (
                                            <div style={{ fontWeight: 'bold', lineHeight: '38px', fontSize: '12px' }}>NO TISS/TIPS</div>
                                        ) : (
                                            <div style={{ border: showStage3TissError ? '1px solid #dc3545' : '1px solid transparent', borderRadius: '4px', padding: showStage3TissError ? '2px' : 0 }}>
                                                <Select2Dropdown
                                                    options={[{ id: 'NO TISS/TIPS', text: 'NO TISS/TIPS' }, { id: 'TISS', text: 'TISS' }, { id: 'TIPS', text: 'TIPS' }]}
                                                    value={stage3Data.tiss_tips}
                                                    onChange={val => { setStage3Data({ ...stage3Data, tiss_tips: val }); if (val) setShowStage3TissError(false); }}
                                                    valueKey="id"
                                                    displayKey="text"
                                                    placeholder="PLEASE SELECT AN OPTION"
                                                    menuPlacement="top"
                                                    popoutMode={popoutMode}
                                                />
                                            </div>
                                        )}
                                        {showStage3TissError && !isGepgPayable && !isCash && <small style={{ color: '#dc3545', display: 'block', marginTop: '4px' }}>TISS / TIPS selection is required.</small>}
                                    </div>
                                    {isTissOrTipsSelected && (
                                        <div style={{ minWidth: '150px' }}>
                                            <small style={{ fontWeight: 'bold', fontSize: '9px' }}>TISS / TIPS AMOUNT (TZS)</small>
                                            <div style={{ fontWeight: 'bold', color: '#c0392b', lineHeight: '38px', fontSize: '12px' }}>{formatCurrency(stage3TissTipsAmount)}</div>
                                        </div>
                                    )}
                                    {isGepgPayable && gepgAmount > 0 && (
                                        <div style={{ minWidth: '150px' }}>
                                            <small style={{ fontWeight: 'bold', fontSize: '9px' }}>GEPG CHARGE (TZS)</small>
                                            <div style={{ fontWeight: 'bold', color: '#c0392b', lineHeight: '38px', fontSize: '12px' }}>{formatCurrency(gepgAmount)}</div>
                                        </div>
                                    )}
                                    {(isTissOrTipsSelected || (isGepgPayable && gepgAmount > 0)) && (
                                        <div style={{ minWidth: '165px' }}>
                                            <small style={{ fontWeight: 'bold', fontSize: '9px' }}>NEW PAYABLE (TZS)</small>
                                            <div style={{ fontWeight: 'bold', color: '#0b5ed7', lineHeight: '38px', fontSize: '12px' }}>{formatCurrency(stage3NewPayableTotal)}</div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {isStage3Approval && (
                                <div style={{ minWidth: '160px' }}>
                                    <small style={{ fontWeight: 'bold', fontSize: '9px' }}>PAYMENT DATE</small>
                                    <input type="date" max={new Date().toISOString().split('T')[0]} className="form-control" value={stage3Data.payment_date} onChange={e => setStage3Data({ ...stage3Data, payment_date: e.target.value })} />
                                </div>
                            )}
                            {canApprove && (
                                <button className="btn btn-success" onClick={isStage2Approval ? handleStage2Approve : handleStage3Approve} disabled={submitting || actionLoading} style={{ backgroundColor: 'green', borderColor: 'green', color: 'white' }}>Approve</button>
                            )}
                            <button className="btn btn-danger" onClick={() => { setRejectReason(''); setRejectionTarget(null); setShowRejectInput(true); }} disabled={actionLoading}>Reject</button>
                            <button className="dialog-btn btn-secondary" onClick={onClose}>Close</button>
                        </>
                    )
                ) : isUploadedReceiptReview ? (
                    showRejectInput ? (
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', width: '100%' }}>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Enter Rejection Reason..."
                                value={rejectReason}
                                onChange={e => setRejectReason(e.target.value)}
                                style={{ flex: 1 }}
                            />
                            <button
                                className="btn btn-danger"
                                onClick={() => onReject(rejectReason, 'REJECTED', {})}
                                disabled={actionLoading}
                            >
                                Confirm Reject
                            </button>
                            <button className="btn btn-secondary" onClick={() => setShowRejectInput(false)}>Cancel</button>
                        </div>
                    ) : (
                        <>
                            {canApprove && (
                                <button className="btn btn-success" onClick={() => onApprove()} disabled={actionLoading} style={{ backgroundColor: 'green', borderColor: 'green', color: 'white' }}>Approve Receipt</button>
                            )}
                            <button className="btn btn-danger" onClick={() => { setRejectReason(''); setShowRejectInput(true); }} disabled={actionLoading}>Reject Receipt</button>
                            <button className="dialog-btn btn-secondary" onClick={onClose}>Close</button>
                        </>
                    )
                ) : (isExpensesView && !isUploadedReceiptReview) ? (
                    <button className="dialog-btn btn-secondary" onClick={onClose}>Close</button>
                ) : approvalMode ? (
                    showRejectInput ? (
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
                            <input type="text" className="form-control" placeholder="Enter Rejection Reason..." value={rejectReason} onChange={e => setRejectReason(e.target.value)} style={{ flex: 1 }} />
                            <button className="btn btn-danger" onClick={executeRejectionSubmit} disabled={actionLoading}>Confirm Reject</button>
                            <button className="btn btn-secondary" onClick={() => { setShowRejectInput(false); setRejectionTarget(null); }}>Cancel</button>
                        </div>
                    ) : (
                        <>
                            {canApprove && (
                                <button className="btn btn-success" onClick={() => onApprove()} disabled={actionLoading} style={{ backgroundColor: 'green', borderColor: 'green', color: 'white' }}>Approve</button>
                            )}
                            <button className="btn btn-danger" onClick={() => { setRejectReason(''); setRejectionTarget(null); setShowRejectInput(true); }} disabled={actionLoading}>Reject</button>
                            <button className="dialog-btn btn-secondary" onClick={onClose}>Close</button>
                        </>
                    )
                ) : (
                    <>
                        {!isView && <button className="dialog-btn btn-primary" onClick={handleSubmit} disabled={submitting || (!formData.job && (module || '').toUpperCase() !== 'ACCOUNTS')}>SAVE PAYABLE</button>}
                        <button className="dialog-btn btn-secondary" onClick={onClose}>CLOSE</button>
                    </>
                )}
            </div>

            {showVendorForm && (
                <div className="modal-overlay" style={{ zIndex: 100000 }}>
                    <div className="modal-content">
                        <button className="modal-close-btn" onClick={() => handleVendorFormClose(false)}>
                            &times;
                        </button>
                        <VendorForm mode="add" initialData={null} onClose={handleVendorFormClose} />
                    </div>
                </div>
            )}

            {/* Confirmation Dialog */}
            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={() => {
                    if (confirmDialog.onConfirm) confirmDialog.onConfirm();
                    closeConfirm();
                }}
                onCancel={closeConfirm}
            />
            <style>{`
                @keyframes flashRedWarning {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.2; }
                }
                .flash-red-warning {
                    color: #d32f2f;
                    font-weight: bold;
                    animation: flashRedWarning 1s infinite;
                }
            `}</style>
        </div>
    );
};

export default OfficePayablesForm;
